import React, { useEffect, useState } from 'react';
import { useSettingsStore } from '../hooks/useSettingsStore';

interface QueryProgressBarProps {
  currentRound: number;
  totalRounds: number;
  isJudgePhase: boolean;
  roundStartTime: number | null;
}

// Default timeout values in milliseconds (fallback if settings not loaded)
const DEFAULT_TIMEOUT_CONFIG = {
  round1: 60000,   // 60 seconds
  round2: 90000,   // 90 seconds (60 * 1.5)
  judge: 90000,    // 90 seconds
};

export default function QueryProgressBar({
  currentRound,
  totalRounds,
  isJudgePhase,
  roundStartTime,
}: QueryProgressBarProps) {
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { settings } = useSettingsStore();

  // Get timeout values based on user settings
  const getTimeoutConfig = () => {
    const userTimeout = (settings?.default_timeout ?? 60) * 1000; // Convert to milliseconds
    return {
      round1: userTimeout,
      round2: Math.floor(userTimeout * 1.5), // 1.5x for round 2
      judge: Math.floor(userTimeout * 1.5),  // 1.5x for judge
    };
  };

  // Determine current phase timeout
  const getCurrentTimeout = () => {
    const timeoutConfig = getTimeoutConfig();
    if (isJudgePhase) return timeoutConfig.judge;
    if (currentRound === 1) return timeoutConfig.round1;
    return timeoutConfig.round2;
  };

  // Get phase label
  const getPhaseLabel = () => {
    if (isJudgePhase) return 'J';
    return currentRound.toString();
  };

  // Get phase color
  const getPhaseColor = () => {
    if (isJudgePhase) return 'bg-yellow-500';
    if (currentRound === 1) return 'bg-blue-500';
    return 'bg-purple-500';
  };

  // Progress bar color based on progress percentage
  const getProgressColor = () => {
    if (progress > 80) return 'bg-red-500';
    if (progress > 60) return 'bg-yellow-500';
    return 'bg-primary-500';
  };

  useEffect(() => {
    if (!roundStartTime) {
      setProgress(0);
      setTimeRemaining(0);
      return;
    }

    const timeout = getCurrentTimeout();

    const updateProgress = () => {
      const elapsed = Date.now() - roundStartTime;
      const pct = Math.min((elapsed / timeout) * 100, 100);
      const remaining = Math.max(0, Math.ceil((timeout - elapsed) / 1000));
      setProgress(pct);
      setTimeRemaining(remaining);
    };

    // Update immediately
    updateProgress();

    // Then update every 100ms
    const interval = setInterval(updateProgress, 100);

    return () => clearInterval(interval);
  }, [roundStartTime, currentRound, isJudgePhase, settings?.default_timeout]);

  if (!roundStartTime) return null;

  return (
    <div className="flex items-center gap-2 w-full px-2 py-1.5 bg-surface-700/50 rounded-lg">
      {/* Phase indicator */}
      <div
        className={`w-6 h-6 rounded-full ${getPhaseColor()} flex items-center justify-center flex-shrink-0`}
        title={isJudgePhase ? 'Judge synthesis' : `Round ${currentRound}`}
      >
        <span className="text-[10px] font-bold text-white">{getPhaseLabel()}</span>
      </div>

      {/* Progress bar container */}
      <div className="flex-1 h-2 bg-surface-600 rounded-full overflow-hidden">
        <div
          className={`h-full ${getProgressColor()} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Time remaining */}
      <span className="text-xs text-surface-400 w-10 text-right flex-shrink-0">
        {timeRemaining}s
      </span>
    </div>
  );
}
